<template>
	<section class="deep-register-view">
		<link rel="stylesheet" href="https://use.typekit.net/rhi6acd.css" />
		<div class="deep-register-parts" v-if="institution">
			<div class="deep-register-emotion">
				<div class="register-images">
					<img class="cover" @error="downgradeImage" :src="image" />
					<img class="logo" :src="institution.logo_url" />
				</div>
				<h1>{{ institution.impact_statement }}</h1>
				<p>
					Your donations allow us to
					{{ institution.full_impact_statement }}.
				</p>
				<p>
					<strong
						>Let's make a one time gift to {{ institution.name }}.
						How much would you like to give?</strong
					>
				</p>
				<div class="donate">
					<form v-on:submit.prevent="onSubmit" class="register-form">
						<segment-toggle
							:value="gift"
							:options="options"
							@on-change="setGift"
							name="donate-now"
							labelPrefix="$"
						/>
						<label
							v-if="gift === 'custom'"
							:style="{ marginTop: '15px', margin: 'auto' }"
						>
							<p :style="{ marginBottom: '5px' }">
								Look at you making an impact! Enter your gift
								below:
							</p>
							<ui-input
								v-model="custom_gift"
								placeholder="$100"
								native-type="number"
								:style="{ width: '50%', display: 'block' }"
								type="blue"
							/>
						</label>
						<label>
							<ui-button :loading="isLoading" type="submit">{{
								institution.call_to_action
							}}</ui-button>
						</label>
					</form>

					<p v-if="isIOS" class="p-button" @click="redirectToApp">
						Back to App
					</p>
				</div>
			</div>
		</div>
		<div v-if="noInstitution">
			<div
				:style="{
					display: flex,
					alignItems: center,
					width: '100%',
					textAlign: 'center'
				}"
			>
				<h1>Hi There!</h1>
				<h2>You've hit a custom page that isn't live.</h2>
				<p>
					This is either because this page isn't ready yet for public
					view, OR the nonprofit is no longer a client. If you
					believed that you received this page in error, please email
					<a href="mailto:support@roundupapp.com"
						>support@roundupapp.com</a
					>.
				</p>

				<p>
					If you're a donor looking to sign up, please register here:
					<a href="https://app.roundupapp.com/#/Register"
						>app.roundupapp.com/register</a
					>
				</p>
			</div>
		</div>
	</section>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import UiButton from '../../ui/Button'
import UiInput from '../../ui/Input'
import SegmentToggle from '../../ui/SegmentToggle'
import { getUrlParams } from '../../utilities'

export default {
	name: 'deep-register-view',
	components: { UiInput, SegmentToggle, UiButton },
	data() {
		return {
			isIOS:
				!!window.navigator.platform &&
				/iPad|iPhone|iPod/.test(window.navigator.platform),
			isLoading: false,
			noInstitution: false,
			message: '',
			options:
				this.cause.type !== 'group'
					? [5, 25, 50, 100, 'custom']
					: [10, 25, 50, 100, 'custom'],
			gift: 25,
			custom_gift: null,
			institution: null,
			current: 0
		}
	},
	computed: {
		title() {
			if (!this.institution) {
				return false
			}
			return (
				'Support ' + this.institution.name + ' By Donating Your Change'
			)
		},
		description() {
			if (!this.institution) {
				return false
			}
			return (
				'Round up and donate your change to support ' +
				this.institution.name +
				'! Sign up in seconds to donate the change from your credit/debit card purchases so ' +
				this.institution.name +
				' can ' +
				this.institution.full_impact_statement +
				'.'
			)
		},
		image() {
			if (!this.institution) {
				return false
			}
			return this.appendToFilename(this.institution.image_url, '@2x')
		},
		...mapGetters(['isLoggedIn', 'cause'])
	},
	head: {
		title() {
			return {
				inner: this.title
			}
		},
		next() {
			this.current = 1
		},
		meta() {
			return [
				{ name: 'description', content: this.description },
				// Twitter
				{
					name: 'twitter:title',
					content: this.title,
					id: 'twitter_title'
				},
				{
					name: 'twitter:description',
					content: this.description,
					id: 'twitter_desc'
				},
				{
					name: 'twitter:image',
					content: this.image,
					id: 'twitter_image'
				},
				// Google+ / Schema.org
				{ itemprop: 'name', content: this.title, id: 'google_title' },
				{
					itemprop: 'description',
					content: this.description,
					id: 'google_desc'
				},
				{ itemprop: 'image', content: this.image, id: 'google_image' },
				// Facebook / Open Graph
				{ property: 'og:title', content: this.title, id: 'fb_title' },
				{
					property: 'og:description',
					content: this.description,
					id: 'fb_desc'
				},
				{ property: 'og:image', content: this.image, id: 'fb_image' }
			]
		}
	},
	methods: {
		...mapActions(['donateNow', 'getHome']),
		...mapState({
			home: ({ home }) => home.item
		}),
		setGift(val) {
			this.gift = val
		},
		redirectToApp() {
			window.location = 'roundupapp://'
		},
		onSubmit() {
			this.isLoading = true
			let amount = this.gift
			if (this.gift === 'custom') {
				amount = this.custom_gift
			}

			if (this.cause.type === 'group' && amount < 10) {
				this.$swal(
					'Error',
					'In order to effectively support multiple organizations, we require a minimum donation of at least $10.'
				)
				return
			}

			const acceptibleErrors = [
				'Your card was declined.',
				'Your card has insufficient funds.',
				'An error occurred with our connection to Stripe.'
			]

			this.donateNow(amount)
				.then(() =>
					this.navigate(
						{ name: 'one-time-donate-success' },
						'forward'
					)
				)
				.catch(({ message, data }) => {
					this.isLoading = false
					let action = 'Retry Donation'
					if (!acceptibleErrors.includes(message)) {
						action = 'Add New Card'
					}

					this.$swal({
						title: 'Uh Oh!',
						text: message,
						showCancelButton: true,
						cancelButtonText: action,
						allowOutsideClick: false,
						allowEscapeKey: false
					}).then((result) => {
						if (result.value) {
							return
						}
						if (acceptibleErrors.includes(message)) {
							this.isLoading = true
							this.reprocessRoundup(data.payoutId).catch(
								(error) => {
									this.isLoading = false
									this.handleErrors(error.message)
								}
							)
						} else {
							this.navigate({ name: 'card' }, 'forward')
						}
					})
				})
		},
		handleErrors() {
			this.$swal({
				title: 'Uh Oh!',
				text: 'Your donation failed twice with this card. We suggest adding a new one.',
				showCancelButton: true,
				cancelButtonText: 'Add New Card',
				allowOutsideClick: false,
				allowEscapeKey: false
			}).then((result) => {
				if (result.value) {
					return
				}
				this.navigate({ name: 'card' }, 'forward')
			})
		},
		appendToFilename(filename, string) {
			var dotIndex = filename.lastIndexOf('.')
			if (dotIndex === -1) return filename + string
			else
				return (
					filename.substring(0, dotIndex) +
					string +
					filename.substring(dotIndex)
				)
		},
		downgradeImage(el) {
			el.srcElement.src = this.institution.image_url
		},
		onBack() {
			this.navigate({ name: this.backRoute }, 'back')
		}
	},
	mounted() {
		const params = getUrlParams(window.location.href)
		if (params.amount) {
			this.gift = params.amount
		} else {
			this.gift = 25
		}

		this.getHome()
			.then((home) => {
				this.institution = home.institution
				this.noInstitution = false
				this.$emit('updateHead')
			})
			.catch(() => {
				this.navigate({ name: 'home' }, 'up')
			})
	}
}
</script>

<style lang="scss">
.deep-register-view {
	width: 960px;
	left: -240px;
	margin-top: 20px;
	text-align: left;
	position: absolute;
	@media screen and (max-width: 960px) {
		width: 100%;
		left: 0px;
	}
	@media screen and (max-width: 480px) {
		margin-top: 0px;
	}
	h1 {
		font-family: ff-tisa-web-pro, sans-serif;
		font-size: 42px;
		font-weight: 500;
		line-height: 48px;
		color: #111;
		margin: 15px 0px 10px;
	}
	h2 {
		font-family: ff-tisa-web-pro, sans-serif;
		font-size: 36px;
		font-weight: 500;
		line-height: 48px;
		color: #111;
		margin: 10px 0px;
	}
	h3 {
		font-family: objektiv-mk3, sans-serif;
		font-size: 22px;
		font-weight: 600;
		color: #111;
		margin: 10px 0px 0px;
	}
	p {
		font-family: objektiv-mk3, sans-serif;
		font-size: 18px;
		font-weight: 400;
		color: #111;
	}
	.powered-by-logo {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		margin-bottom: 10px;
		text-align: center;
		p {
			color: $dark-gray;
			font-size: 12px;
			line-height: 10px;
		}
	}
	.deep-register-parts {
		display: flex;
		justify-content: center;
		@media screen and (max-width: 960px) {
			display: block;
		}
	}
	.register-images {
		position: relative;
		margin-left: -40px;
		margin-right: -40px;
		margin-top: -30px;
		height: 275px;
		overflow: hidden;
		.cover {
			height: 100%;
			max-width: 100%;
			-o-object-fit: cover;
			object-fit: cover;
			width: 100%;
		}
		img {
			border-radius: 10px 10px 0px 0px;
			width: 100%;
			@media screen and (max-width: 480px) {
				border-radius: 0px;
			}
		}
		.logo {
			max-width: 75px;
			position: absolute;
			left: 40px;
			bottom: 15px;
			border-radius: 10px;
		}
	}
	.donate {
		color: #fff;
		.p-button {
			cursor: pointer;
			color: $dark-grey;
			font-size: 14px;
			line-height: 25px;
			&:hover {
				text-decoration: underline;
			}
		}
	}
	.deep-register-half {
		padding: 0px 15px;
	}
	.deep-register-emotion {
		background: #fff;
		border-radius: 10px;
		box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
		padding: 30px 40px;
		width: 60%;
		flex-shrink: 0;
		@media screen and (max-width: 960px) {
			width: auto;
		}
		@media screen and (max-width: 480px) {
			border-radius: 0px;
		}
		.register-form {
			width: 100%;
			label {
				display: block;
				margin-top: 5px;
				margin-bottom: 5px;
			}
			.terms {
				font-size: 14px;
				color: #888;
				margin-bottom: 0px;
			}
		}
	}
	.deep-register-steps {
		padding: 30px 10px 30px 30px;
		@media screen and (max-width: 480px) {
			padding: 30px;
		}
		.step {
			vertical-align: top;
			padding: 10px 0px;
			.columns {
				display: flex;
			}
			p {
				margin-top: 5px;
			}
			img {
				width: 25%;
				height: 25%;
			}
		}
	}
	.roundup-button {
		width: auto;
		max-width: 450px;
		margin-top: 20px;
		position: relative;
		text-decoration: none;
		background: #184fd5;
		color: #fff;
		cursor: pointer;
		display: inline-block;
		font-family: objektiv-mk3, sans-serif;
		font-size: 14px;
		font-weight: 600;
	}
}
</style>
